import type { FetchResult } from "./opsPaperworkClientBase";
import { supabase } from "./supabase";
import { readWorkspaceOrgIdFromStorage } from "./workspaceOrgStorage";

const BASE = "/api/ops/inbound-webhooks";

async function inboundFetch<T>(init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string> | undefined),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) headers["X-Workspace-Organization-Id"] = ws;
  const r = await fetch(BASE, { ...init, headers });
  const json = (await r.json().catch(() => null)) as T & { error?: string };
  if (!r.ok) {
    return { ok: false, status: r.status, error: json?.error || `HTTP ${r.status}` };
  }
  return { ok: true, status: r.status, data: json };
}

export type OpsInboundWebhook = {
  id: string;
  slug: string;
  display_name: string;
  is_active: boolean;
  is_builtin: boolean;
  created_at: string;
  deactivated_at: string | null;
};

export async function listOpsInboundWebhooks(): Promise<
  FetchResult<{ ok: true; items: OpsInboundWebhook[]; can_deactivate: boolean }>
> {
  return inboundFetch({ method: "GET" });
}

export async function createOpsInboundWebhook(
  displayName: string
): Promise<FetchResult<{ ok: true; item: OpsInboundWebhook }>> {
  return inboundFetch({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ display_name: displayName }),
  });
}

export async function setOpsInboundWebhookActive(
  id: string,
  isActive: boolean
): Promise<FetchResult<{ ok: true; item: OpsInboundWebhook }>> {
  return inboundFetch({
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, is_active: isActive }),
  });
}
